import { Link } from 'react-router-dom';
import { REPORT_MODULE_MAP, type ReportModule } from '@goyone/shared';
import { Icon } from '../components/Icon';
import { ErrorState, PageHead } from '../components/ui';
import { DashboardBoard, toBoardCards, usePublishedReports } from '../components/DashboardBoard';

/**
 * لوحةُ وحدة — تقاريرُها وإحصاءاتُها في مكانٍ واحد.
 *
 * كلُّ وحدةٍ لها لوحتُها: بطاقاتٌ جاهزة تخصّها، وما نشره الفريقُ عليها من
 * الباني، وروابطُ تحليلاتها الخاصّة (القمع، الخسائر، القوائم المالية…).
 * والرئيسيةُ تجمع من كلِّها لقطةً عن الشركة كلِّها.
 */
/**
 * لوحةُ الوحدة — إمّا صفحةً كاملة، وإمّا مضمَّنةً تحت تبويبٍ في الرئيسية.
 *
 * `embedded` يُسقط ترويسةَ الصفحة (فالرئيسيةُ تحملها) ويبقي روابطَ
 * التحليلات واللوحةَ — فلا تُكرَّر الترويسةُ ولا يُلفّ في `page` داخل `page`.
 */
export function ModuleDashboard({
  module,
  embedded = false,
}: {
  module: ReportModule;
  embedded?: boolean;
}) {
  const def = REPORT_MODULE_MAP[module];
  const published = usePublishedReports({ module });

  const analytics =
    def.analytics && def.analytics.length > 0 ? (
      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        {def.analytics.map((a) => (
          <Link key={a.path} to={a.path} className="btn btn-sm">
            <Icon name="chart" size={14} />
            {a.label}
          </Link>
        ))}
      </div>
    ) : null;

  const body = (
    <>
      {embedded && analytics && <div className="mb-4">{analytics}</div>}

      {published.isError && (
        <ErrorState error={published.error} onRetry={() => void published.refetch()} />
      )}
      {published.isPending && <div className="skeleton" style={{ height: 240 }} />}

      {published.data && (
        <DashboardBoard
          storageKey={`goyone.dash.${module}`}
          cards={toBoardCards(def.cards, published.data.items)}
          addTo={`/reports/builder?module=${module}`}
        />
      )}
    </>
  );

  if (embedded) return body;

  return (
    <div className="page">
      <PageHead
        title={`لوحة ${def.label}`}
        subtitle={def.subtitle}
        actions={analytics ?? undefined}
      />
      {body}
    </div>
  );
}
